import { Icon } from '../../atoms/Icon';
import Checkbox from '../../atoms/Checkbox';
import Radio from '../../atoms/Radio';
import { ExerciseStatus, ExerciseType } from '../../../models/exercise';
import { LoadingIcon } from 'morpheus-ui';
import { Button } from '../../atoms/Button';
import { useMemo, useState } from 'react';

// Remove, APENAS do final do HTML, tags cujo conteúdo é só vazio, espaços,
// &nbsp; ou <br> (ex.: <p></p>, <p><br></p>, <div>&nbsp;</div>) e quebras de
// linha soltas. Não mexe em nada que esteja no meio do conteúdo.
const cleanTrailingEmptyHtml = (html?: string): string => {
  if (!html) return '';
  let cleaned = html.trim();

  const trailingEmptyTag =
    /\s*<([a-zA-Z][\w-]*)\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/\1>\s*$/i;

  // repete para casos aninhados (ex.: <div><p></p></div>) e várias tags no fim
  while (trailingEmptyTag.test(cleaned)) {
    cleaned = cleaned.replace(trailingEmptyTag, '').trim();
  }

  // <br> / espaços / &nbsp; soltos no fim (fora de tag)
  cleaned = cleaned.replace(/(?:\s|&nbsp;|<br\s*\/?>)+$/i, '').trim();

  return cleaned;
};

interface ExerciseAlternativeProps {
  isShowingCorrectAnswer?: boolean;
  alternativeId: string;
  text: string;
  value: string;
  type?: ExerciseType;
  status?: ExerciseStatus;
  selected?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  isStrikeout?: boolean;
  isEmptyExercise?: boolean;
  isCorrect?: boolean;
  flushTopLeft?: boolean;
  onSelected: () => void;
  onStrikeout?: () => void;
}

const ExerciseAlternative = ({
  isShowingCorrectAnswer = false,
  isCorrect = false,
  alternativeId,
  text,
  value,
  type = 'single_answer',
  status = 'default',
  selected = false,
  disabled = false,
  isLoading = false,
  isStrikeout = false,
  isEmptyExercise = false,
  flushTopLeft = false,
  onSelected,
  onStrikeout,
}: ExerciseAlternativeProps) => {
  const [isStriked, setIsStriked] = useState<boolean>(isStrikeout);

  const cleanedText = useMemo(() => cleanTrailingEmptyHtml(text), [text]);

  const colorStyle = {
    default: 'bg-mono-color-white',
    selected: 'bg-brand-color-primary-lighter border-mono-color-white border-hairline',
    success: 'bg-feedback-color-success-lightest border-mono-color-white border-hairline',
    error: 'bg-feedback-color-error-lightest border-mono-color-white border-hairline',
    correct: 'bg-mono-color-light border-mono-color-white border-hairline',
  };

  const internalStatus = useMemo(() =>{
    if (!selected && !isShowingCorrectAnswer) return "default"

    if (isShowingCorrectAnswer && isEmptyExercise && isCorrect) return 'success';

    return status;
  }, [selected, status]);

  const boxColorStyle = selected && internalStatus === 'default' ? colorStyle['selected'] : colorStyle[internalStatus];

  const isLoadingSelected =
    isLoading &&
    selected &&
    internalStatus !== 'success' &&
    internalStatus !== 'error';

  let checkedColorType: 'default' | 'success' | 'error' | 'correct' = 'default';

  if (
    internalStatus === 'success' ||
    internalStatus === 'error' ||
    internalStatus === 'correct'
  )
    checkedColorType = internalStatus;

  return (
    <div
      className='relative flex cursor-pointer flex-col gap-sm'
      onClick={() => {
        setIsStriked(false);
        isLoading || disabled ? null : onSelected();
      }}
    >
      {isLoadingSelected && (
        <LoadingIcon
          name='progress_activity'
          className='stroke-current absolute bottom-0 left-0 right-0 top-0 m-auto h-6 animate-spin fill-none text-brand-color-primary-pure'
        />
      )}
      <div
        className={`flex items-center justify-between rounded-md p-4 shadow-level-1 ${boxColorStyle} ${
          isLoadingSelected ? 'opacity-30' : ''
        } ${flushTopLeft ? 'rounded-tl-none' : ''}`}
      >
        <div
          className={`flex items-center gap-lg ${
            isStriked && !disabled ? 'text-mono-color-darkest line-through' : ''
          }`}
        >
          {type === 'single_answer' ? (
            <Radio
              id={alternativeId}
              label={cleanedText}
              value={value}
              checked={selected}
              checkedColorType={checkedColorType}
              onChange={() => {}}
              disabled={isLoading || disabled}
            />
          ) : (
            <Checkbox
              id={alternativeId}
              value={value}
              label={cleanedText}
              checked={selected}
              checkedColorType={checkedColorType}
              onChange={() => {}}
              disabled={isLoading || disabled}
            />
          )}
        </div>
        {internalStatus === 'default' && !selected && !disabled && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setIsStriked(!isStriked);
              onStrikeout && onStrikeout();
            }}
            variant='text'
            LeftIcon={isStriked ? 'check_circle' : 'cancel'}
            iconSize={'small'}
            onlyIcon
          />
        )}
        {internalStatus === 'success' && (
          <Icon
            Icon='check'
            type='material'
            className='text-base text-feedback-color-success-darkest'
          />
        )}
        {internalStatus === 'error' && (
          <Icon
            Icon='close'
            type='material'
            className='text-base text-feedback-color-error-darkest'
          />
        )}
      </div>
    </div>
  );
};

export default ExerciseAlternative;
